import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { resetPassword } from "../services/authService";

// The exact three token-status messages the backend can return (see
// routers/auth_router.py's reset_password()) — matched verbatim so this
// page can tell "the token itself is dead, there is nothing left to fix
// here" apart from a fixable form error (password mismatch, too short,
// policy violation, network hiccup), which stays inline and retryable.
const TOKEN_DEAD_MESSAGES = new Set([
  "Invalid or expired password reset link.",
  "This password reset link has expired. Please request a new one.",
  "This password reset link is no longer valid.",
]);

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Independent visibility per field — showing one must never reveal the other.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tokenDeadMessage, setTokenDeadMessage] = useState(
    token ? "" : "Invalid or expired password reset link."
  );
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await resetPassword({ token, newPassword: password });
      setSuccess(true);
    } catch (err) {
      if (!err?.response) {
        setError("Unable to connect to the server. Please check your connection and try again.");
      } else {
        const msg = err.response.data?.detail;
        const text = typeof msg === "string" ? msg : "Unable to reset your password. Please try again.";
        if (TOKEN_DEAD_MESSAGES.has(text)) {
          setTokenDeadMessage(text);
        } else {
          setError(text);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-sm mx-auto px-4 py-16 text-center">
        <div className="rounded-2xl border border-green-200 bg-green-50 p-8">
          <h1 className="text-lg font-semibold text-green-800">Password Reset Successfully</h1>
          <p className="mt-2 text-sm text-neutral-700">Your password has been changed.</p>
        </div>
        <Link
          to="/login"
          className="mt-6 inline-block w-full bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-3 hover:bg-neutral-800"
        >
          Go to Login
        </Link>
      </div>
    );
  }

  if (tokenDeadMessage) {
    return (
      <div className="max-w-sm mx-auto px-4 py-16 text-center">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8">
          <p className="text-sm text-red-700">{tokenDeadMessage}</p>
        </div>
        <Link to="/forgot-password" className="mt-6 inline-block text-sm text-neutral-600 underline">
          Request a new reset link
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto px-4 py-16">
      <h1 className="text-xl font-semibold text-neutral-900 mb-6 text-center">Reset Password</h1>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="rp-password" className="block text-sm font-medium text-neutral-700 mb-1">
            New Password
          </label>
          <div className="relative">
            <input
              id="rp-password"
              type={showPassword ? "text" : "password"}
              required
              minLength={8}
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 pl-3 pr-11 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-neutral-400 hover:text-neutral-700"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className="mt-1 text-xs text-neutral-400">At least 8 characters.</p>
        </div>
        <div>
          <label htmlFor="rp-confirm" className="block text-sm font-medium text-neutral-700 mb-1">
            Confirm New Password
          </label>
          <div className="relative">
            <input
              id="rp-confirm"
              type={showConfirmPassword ? "text" : "password"}
              required
              minLength={8}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 pl-3 pr-11 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-neutral-400 hover:text-neutral-700"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {confirmPassword && password !== confirmPassword && (
            <p className="mt-1 text-xs text-red-600">Passwords do not match.</p>
          )}
        </div>

        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-3 hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Resetting..." : "Reset Password"}
        </button>
      </form>

      <p className="text-sm text-neutral-500 text-center mt-6">
        <Link to="/login" className="text-neutral-900 underline">
          Back to Login
        </Link>
      </p>
    </div>
  );
}
